import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  BannerData,
  BannerResolvedStyles,
  ComponentInstance,
  ComponentType,
  PopupData,
  PopupResolvedStyles,
  ProcessedComponentInstance
} from '../types';
import resolveVariantStyles from '../utils/resolveVariantStyles';

interface DynamicComponentsState {
  banners: ProcessedComponentInstance<BannerData, BannerResolvedStyles>[];
  feStaticBanners: ProcessedComponentInstance<
    BannerData,
    BannerResolvedStyles
  >[];
  popups: ProcessedComponentInstance<PopupData, PopupResolvedStyles>[];
  bannerIndex: number;
  popupQueueIndex: number;
  isLoaded: boolean;
}

const initialState: DynamicComponentsState = {
  banners: [],
  feStaticBanners: [],
  popups: [],
  bannerIndex: 0,
  popupQueueIndex: 0,
  isLoaded: false
};

const dynamicComponentsSlice = createSlice({
  name: 'dynamicComponents',
  initialState,
  reducers: {
    setComponents: (
      state,
      action: PayloadAction<{
        banners: ComponentInstance<BannerData>[];
        popups: ComponentInstance<PopupData>[];
      }>
    ) => {
      state.banners = action.payload.banners.map((b) => ({
        ...b,
        resolvedStyles: resolveVariantStyles(
          b,
          ComponentType.BANNER
        ) as BannerResolvedStyles
      }));
      state.popups = action.payload.popups.map((p) => ({
        ...p,
        resolvedStyles: resolveVariantStyles(
          p,
          ComponentType.POPUP
        ) as PopupResolvedStyles
      }));
      const totalCount = state.banners.length + state.feStaticBanners.length;
      if (state.bannerIndex >= totalCount) {
        state.bannerIndex = 0;
      }
      state.popupQueueIndex = 0;
      state.isLoaded = true;
    },
    dismissBanner: (state, action: PayloadAction<string>) => {
      const allBanners = [...state.banners, ...state.feStaticBanners];
      const idx = allBanners.findIndex((b) => b.id === action.payload);
      if (idx === -1) return;

      state.banners = state.banners.filter((b) => b.id !== action.payload);
      state.feStaticBanners = state.feStaticBanners.filter(
        (b) => b.id !== action.payload
      );

      const totalCount = state.banners.length + state.feStaticBanners.length;
      if (totalCount === 0) {
        state.bannerIndex = 0;
      } else {
        state.bannerIndex = idx >= totalCount ? 0 : idx;
      }
    },
    setBannerIndex: (state, action: PayloadAction<number>) => {
      const totalCount = state.banners.length + state.feStaticBanners.length;
      if (action.payload >= 0 && action.payload < totalCount) {
        state.bannerIndex = action.payload;
      }
    },
    dismissPopup: (state, action: PayloadAction<string>) => {
      state.popups = state.popups.filter((p) => p.id !== action.payload);
      state.popupQueueIndex = 0;
    },
    advancePopupQueue: (state) => {
      if (state.popups.length > 1) {
        state.popups = state.popups.slice(1);
        state.popupQueueIndex = 0;
      } else {
        state.popups = [];
        state.popupQueueIndex = 0;
      }
    },
    setFeStaticBanners: (
      state,
      action: PayloadAction<ComponentInstance<BannerData>[]>
    ) => {
      state.feStaticBanners = action.payload.map((b) => ({
        ...b,
        resolvedStyles: resolveVariantStyles(
          b,
          ComponentType.BANNER
        ) as BannerResolvedStyles
      }));
    }
  }
});

export const {
  setComponents,
  dismissBanner,
  setBannerIndex,
  dismissPopup,
  advancePopupQueue,
  setFeStaticBanners
} = dynamicComponentsSlice.actions;

export const selectAllBanners = createSelector(
  [
    (state: { dynamicComponents: DynamicComponentsState }) =>
      state.dynamicComponents.banners,
    (state: { dynamicComponents: DynamicComponentsState }) =>
      state.dynamicComponents.feStaticBanners
  ],
  (banners, feStaticBanners) => [...banners, ...feStaticBanners]
);

export default dynamicComponentsSlice;
